import axios from "axios";
import * as CryptoJS from "crypto-js";
import { saveAs } from "file-saver";

import { UserContextTypes } from "../userContext/redux/userContext.reducer";
import { CookieName } from "../app/cookiePopup/cookiePopup.constants";
import { isCookieEnabled } from "../helpers/isCookieEnabled";
import { store } from "../store";
import { AppConfig } from "../appConfig";
import { SET_NETWORK_ERROR } from "../app/networkErrorModal/actions";
import { clearApiCache } from "../helpers/clearApiCache";
import { contentDispositionHeaderFilenameRegexp, fileExtensionRegexp } from "../regexps";

const keyhex = process.env.REACT_APP_KEYHEX;

const encodeRequest = (plainText) => {
  const salt = CryptoJS.lib.WordArray.random(16);
  const iv = CryptoJS.lib.WordArray.random(16);
  const key = CryptoJS.PBKDF2(keyhex, salt, { hasher: CryptoJS.algo.SHA512, keySize: 64 / 8, iterations: 128 });
  const encrypted = CryptoJS.AES.encrypt(plainText, key, { iv: iv });

  return (
    CryptoJS.enc.Hex.stringify(iv) +
    CryptoJS.enc.Hex.stringify(salt) +
    CryptoJS.enc.Base64.stringify(encrypted.ciphertext)
  );
}

const decodeResponse = (responseData) => {
  const response = {
    iv: responseData.substr(0,32),
    salt: responseData.substr(32,32),
    ciphertext: responseData.substr(64),
  };
  const encrypted = response.ciphertext;
  const salt = CryptoJS.enc.Hex.parse(response.salt);
  const iv = CryptoJS.enc.Hex.parse(response.iv);
  const key = CryptoJS.PBKDF2(keyhex, salt, { hasher: CryptoJS.algo.SHA512, keySize: 64 / 8, iterations: 128 });
  const decrypted = CryptoJS.AES.decrypt(encrypted, key, { iv: iv });
  const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
  return JSON.parse(decryptedString);
};

const requestInterceptor = async (config) => {
  const state = store.getState();
  const brand = state.appSettings.currentBrand;

  if (AppConfig.encryptMode && config.params && !config.customEnv?.notEncryptedRequest) {
    config.url += `?payload=${encodeRequest(JSON.stringify(config.params))}`;
    delete config.params;
  }

  if (AppConfig.encryptMode && config.data && config.method !== 'get' && !config.customEnv?.notEncryptedRequest) {
    config.data = encodeRequest(JSON.stringify(config.data));
    config.headers['content-type']='text/plain';
  }

  config.headers["LANGUAGE"] = isCookieEnabled(CookieName.Functional) && localStorage.getItem("Lng")
    ? localStorage.getItem("Lng")
    : "pl";


  if (brand) {
    config.headers["BRAND"] = brand;
  }

  return config;
};

const responseInterceptor = (response) => {
  const config = response?.config;
  const isBinary = config?.responseType === 'arraybuffer' || config?.responseType === 'blob';
  if (response && response.data && AppConfig.encryptMode && !isBinary && !config.customEnv?.notEncryptedResponse) {
    response.data = decodeResponse(response.data);
  }
  if (isBinary && !config.customEnv?.skipSaveAs) {
    const contentDispositionHeader = response.headers['content-disposition'] ?? '';
    const matches = contentDispositionHeader.match(contentDispositionHeaderFilenameRegexp);
    let finalFilename = config?.customEnv?.filename ?? 'file1';

    if ( matches?.[1]) {
      finalFilename = matches[1].replace(/['"]/g, '');
      const extensionFromMatches = finalFilename.match(fileExtensionRegexp);

      if (config?.customEnv?.filename && extensionFromMatches?.[0]) {
        finalFilename = `${config.customEnv?.filename}${extensionFromMatches[0]}`;
      }
    }
    let blob = '';
    try {
      blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
    } catch(e) {
      console.error(e);
    }

    saveAs(blob, finalFilename);
  }

  return response;
};

const responseErrorInterceptor = (error) => {
  if(error.response && error.response.data && AppConfig.encryptMode){
    error.response.data = decodeResponse(error.response.data);
  }
  if (error.toString().indexOf("Network Error") !== -1) {
    store.dispatch({ type: SET_NETWORK_ERROR, error: true });
  }
  return Promise.reject(error);
};

export const nodeApi = axios.create({
  baseURL: AppConfig.nodeApi,
  responseType: "json",
});

nodeApi.interceptors.request.use(requestInterceptor);
nodeApi.interceptors.response.use(responseInterceptor, responseErrorInterceptor);

export const phpApi = axios.create({
  baseURL: AppConfig.api,
  responseType: "json",
});

phpApi.interceptors.request.use(requestInterceptor);
phpApi.interceptors.response.use(responseInterceptor, responseErrorInterceptor);

export const handleApiError = (error, redirectTo) => {
  const { response } = error;
  if (response) {
    if (response.status !== 401 && response.data?.message) {
      alert(response.data.message);
    }
    if (response.status === 403) {
      window.location.replace(redirectTo || "/");
    }
    if (response.status === 409) {
      window.location.reload();
    }
  }
  if (error.toString().indexOf("Network Error") !== -1) {
    store.dispatch({ type: SET_NETWORK_ERROR, error: true });
  }
  return [];
};

export const handleOnboardingApiError = (error) => {
  const { response } = error;
  if (response && response.status === 403) {
    if (response.data && response.data.message) {
      alert(response.data.message);
    }
    store.dispatch({ type: UserContextTypes.SET_LOGGED_OUT, data: {} });
    clearApiCache();
    window.location.replace("/");
  } else {
    handleApiError(error);
  }

  return [];
}

export const setAuthToken = (token) => {
   if (token) {
    phpApi.defaults.headers["X-AUTH-TOKEN"] = token;
    nodeApi.defaults.headers["X-AUTH-TOKEN"] = token;
  } else {
    delete phpApi.defaults.headers["X-AUTH-TOKEN"];
    delete nodeApi.defaults.headers["X-AUTH-TOKEN"];
  }
};

export const setBaseApiUrl = (basePhpApiURL, baseNodeApiUrl) => {
  phpApi.defaults.baseURL = basePhpApiURL;
  nodeApi.defaults.baseURL = baseNodeApiUrl;
};
